"use client";

import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import {
  __LIVE_LANDING_PAGE__,
  livelandingPageComponents,
} from "../../constants/livelanding-page.constants";
import type { MediaData } from "../../types/livelanding.types";
import type { LiveStream as UpcomingLiveStream } from "../../types/upcoming-live.types";
import { logReminderInfo } from "../../utils/reminderLogger";
import { defaultReminderService } from "../../utils/reminderServiceAdapter";
import { DynamicSkeletonLoader, Skeleton } from "../SkeletonLoader/SkeletonLoader";
import { LiveSessionCardLive } from "../UpcomingLive/LiveLandingPageWrapper";
import type { ReminderService } from "../UpcomingLive/UpcomingLive";
import { useReminders } from "../UpcomingLive/useReminders";

const MobileSkeleton = () => (
  <div className="flex [scrollbar-width:none] gap-[8px] overflow-x-auto [&::-webkit-scrollbar]:hidden">
    {Array.from({ length: 4 }).map((_, i) => (
      <div
        key={`mobile-skeleton-${i}`}
        className="min-w-[260px] shrink-0 max-[370px]:min-w-[240px]"
      >
        <Skeleton
          variant="rectangular"
          className="mb-2 h-[200px] w-full rounded-[8px] bg-gray-200"
          aria-hidden="true"
        />
        <Skeleton variant="text" className="mb-1 h-[14px] w-[75%] bg-gray-200" aria-hidden="true" />
        <Skeleton variant="text" className="h-[12px] w-[50%] bg-gray-200" aria-hidden="true" />
      </div>
    ))}
  </div>
);

const ShowMoreButton = ({
  onClick,
  ariaLabel,
  ariaControls,
  ariaExpanded,
  loading = false,
}: {
  onClick: () => void;
  ariaLabel: string;
  ariaControls: string;
  ariaExpanded: boolean;
  loading?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className="w-full! text-center! font-medium! text-[#FF6A00]! disabled:cursor-not-allowed disabled:opacity-50"
    aria-label={ariaLabel}
    aria-expanded={ariaExpanded}
    aria-controls={ariaControls}
    aria-busy={loading}
  >
    {loading ? (
      <span className="inline-flex items-center gap-1 text-[14px]">
        <svg
          className="inline-block h-[16px] w-[16px] animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          />
        </svg>
        Loading...
      </span>
    ) : (
      <>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="currentColor"
          focusable="false"
          aria-hidden="true"
          className="inline-block!"
        >
          <path d="M6.3 8.3L12 14l5.7-5.7 1.4 1.4L12 16.8 4.9 9.7z" />
        </svg>
        <span className="text-[14px]">{livelandingPageComponents.SHOW_MORE}</span>
      </>
    )}
  </button>
);

const ShowLessButton = ({
  onClick,
  ariaLabel,
  ariaControls,
}: {
  onClick: () => void;
  ariaLabel: string;
  ariaControls: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full! text-center! font-medium! text-[#FF6A00]!"
    aria-label={ariaLabel}
    aria-expanded={true}
    aria-controls={ariaControls}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      focusable="false"
      aria-hidden="true"
      className="inline-block!"
    >
      <path d="M6.3 15.7L12 10l5.7 5.7 1.4-1.4L12 7.2 4.9 14.3z" />
    </svg>
    <span className="text-[14px]">{livelandingPageComponents.SHOW_LESS}</span>
  </button>
);

export interface LivePlaylistMobileBlockProps {
  streams: MediaData["live_streams"];
  isUpcoming: boolean;
  loading: boolean;
  emptyMessage: string;
  title: string;
  loadingLabel: string;
  showShowMoreOnMobile: boolean;
  visibleCount: number;
  handleShowMore: () => void;
  handleShowLess: () => void;
  initialVisibleCount: number;
  sectionId: string;
  showMobileList: boolean;
  setReminderText: string;
  reminderSetText: string;
  sectionTitle?: string;
  sectionTitleCt?: string;
  influencerId?: string;
  reminderService?: ReminderService;
  /** Called when horizontal scroll reaches near the end (playlist mode only) */
  onScrollEnd?: () => void;
  /** Whether a next-page fetch is in progress */
  isFetchingMore?: boolean;
  /** Whether a Show More fetch is in progress */
  isFetchingShowMore?: boolean;
  /** Whether there are more pages to fetch */
  hasMore?: boolean;
}

const LivePlaylistMobileBlockComponent = ({
  streams,
  isUpcoming,
  loading,
  emptyMessage,
  title,
  loadingLabel,
  showShowMoreOnMobile,
  visibleCount,
  handleShowMore,
  handleShowLess,
  initialVisibleCount,
  sectionId,
  showMobileList,
  setReminderText,
  reminderSetText,
  sectionTitle,
  sectionTitleCt,
  influencerId,
  reminderService: reminderServiceProp,
  onScrollEnd,
  isFetchingMore = false,
  isFetchingShowMore = false,
  hasMore = false,
}: LivePlaylistMobileBlockProps) => {
  const scrollRef = useRef<HTMLUListElement | null>(null);
  const mobileListId = isUpcoming ? "upcoming-live-list-mobile" : `${sectionId}-list-mobile`;

  const reminderService = useMemo(() => {
    if (!isUpcoming) return undefined;
    return reminderServiceProp ?? defaultReminderService;
  }, [isUpcoming, reminderServiceProp]);

  const { reminders, handleReminderToggle } = useReminders(
    reminderService,
    isUpcoming ? (influencerId ?? __LIVE_LANDING_PAGE__) : undefined
  );

  useEffect(() => {
    if (!isUpcoming) return;
    logReminderInfo("LivePlaylistMobileBlock", "reminders:map-updated", {
      isUpcoming,
      streamsCount: streams.length,
      remindersSize: reminders.size,
      reminderVideoIds: Array.from(reminders.keys()),
      reminders,
    });
  }, [isUpcoming, reminders, streams.length]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !onScrollEnd || !hasMore) return;
    const threshold = 150;
    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - threshold) {
      onScrollEnd();
    }
  }, [onScrollEnd, hasMore]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || isUpcoming || sectionId === "trending-now-title") return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll, isUpcoming, sectionId]);

  const MobileContent = isUpcoming ? (
    <ul
      id={mobileListId}
      className="block list-none md:hidden"
      aria-label="Upcoming live sessions (mobile)"
      aria-busy={loading}
      aria-live="polite"
    >
      {streams.slice(0, visibleCount).map((session) => {
        const reminder = session.id ? reminders.get(session.id) : undefined;

        logReminderInfo("LivePlaylistMobileBlock", "reminders:session->card-props", {
          sessionId: session.id,
          hasReminder: Boolean(reminder),
          reminderIsActive: reminder?.isActive,
          reminder,
        });

        return (
          <li key={session.id} className="mb-4! list-none">
            <LiveSessionCardLive
              session={session as unknown as UpcomingLiveStream}
              bordernone="border-none"
              setReminderText={setReminderText}
              reminderSetText={reminderSetText}
              isReminderSet={Boolean(reminder)}
              onReminderToggle={handleReminderToggle}
              reminderService={reminderService}
              title={sectionTitle}
              titleCt={sectionTitleCt}
            />
          </li>
        );
      })}
      {showShowMoreOnMobile && visibleCount < streams.length && (
        <li className="list-none">
          <ShowMoreButton
            onClick={handleShowMore}
            ariaLabel="Show more upcoming live sessions"
            ariaControls={mobileListId}
            ariaExpanded={visibleCount >= streams.length}
            loading={isFetchingShowMore}
          />
        </li>
      )}
      {showShowMoreOnMobile && !hasMore && visibleCount > initialVisibleCount && (
        <li className="list-none">
          <ShowLessButton
            onClick={handleShowLess}
            ariaLabel="Show less upcoming live sessions"
            ariaControls={mobileListId}
          />
        </li>
      )}
    </ul>
  ) : (
    <>
      {sectionId === "trending-now-title" && streams.length > 0 && (
        <>
          {streams.slice(0, visibleCount).map((session) => (
            <div key={session.id} className="mb-[8px]">
              <LiveSessionCardLive
                session={session as unknown as UpcomingLiveStream}
                bordernone="border-none"
                isFeaturedLiveCard
                enableDynamicPlayer
                title={sectionTitle}
                titleCt={sectionTitleCt}
              />
            </div>
          ))}
        </>
      )}
      {sectionId !== "trending-now-title" && streams.length > 0 && (
        <ul
          ref={scrollRef}
          className="flex [scrollbar-width:none] gap-[8px] overflow-x-auto scroll-smooth [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          aria-live="polite"
        >
          {streams.map((session) => (
            <li key={session.id} className="shrink-0">
              <LiveSessionCardLive
                session={session as unknown as UpcomingLiveStream}
                bordernone="border-none"
                isFeaturedLiveCard
                enableDynamicPlayer
                title={sectionTitle}
                titleCt={sectionTitleCt}
              />
            </li>
          ))}
          {isFetchingMore && (
            <li
              className="min-w-[260px] shrink-0 max-[370px]:min-w-[240px]"
              aria-live="polite"
              aria-label="Loading more videos"
            >
              <Skeleton
                variant="rectangular"
                className="mb-2 h-[200px] w-full rounded-[8px] bg-gray-200"
                aria-hidden="true"
              />
              <Skeleton
                variant="text"
                className="mb-1 h-[14px] w-[75%] bg-gray-200"
                aria-hidden="true"
              />
              <Skeleton
                variant="text"
                className="h-[12px] w-[50%] bg-gray-200"
                aria-hidden="true"
              />
            </li>
          )}
        </ul>
      )}
      {showShowMoreOnMobile && (visibleCount < streams.length || hasMore) && (
        <ShowMoreButton
          onClick={handleShowMore}
          ariaLabel="Show more live sessions"
          ariaControls={mobileListId}
          ariaExpanded={visibleCount >= streams.length && !hasMore}
          loading={isFetchingShowMore}
        />
      )}
      {showShowMoreOnMobile && !hasMore && visibleCount > initialVisibleCount && (
        <ShowLessButton
          onClick={handleShowLess}
          ariaLabel="Show less live sessions"
          ariaControls={mobileListId}
        />
      )}
    </>
  );

  const renderMobileBlockContent = () => {
    if (loading && isUpcoming && streams.length > 0) {
      return (
        <ul
          className="block list-none md:hidden"
          aria-label="Loading upcoming live sessions (mobile)"
          aria-busy={loading}
        >
          {Array.from({
            length: Math.min(visibleCount, streams.length),
          }).map((_, i) => (
            <li key={`mobile-loading-${i}`} className="mb-4 list-none">
              <Skeleton
                variant="rectangular"
                className="h-[200px] w-full rounded-lg bg-gray-200 sm:h-[190px]"
                aria-hidden="true"
              />
            </li>
          ))}
        </ul>
      );
    }

    if (loading && !isUpcoming) {
      return (
        <DynamicSkeletonLoader aria-busy="true" aria-label={loadingLabel}>
          <span className="sr-only">Loading {title} videos, please wait...</span>
          <MobileSkeleton />
        </DynamicSkeletonLoader>
      );
    }

    if (!loading) {
      return (
        <>
          {sectionId === "trending-now-title" && !isUpcoming && streams.length === 0 && (
            <output className="mt-4 block text-center text-gray-500" aria-live="polite">
              {emptyMessage}
            </output>
          )}
          {MobileContent}
          {!isUpcoming && sectionId !== "trending-now-title" && streams.length === 0 && (
            <output className="mt-4 block text-center text-gray-500" aria-live="polite">
              {emptyMessage}
            </output>
          )}
        </>
      );
    }

    return null;
  };

  if (!showMobileList) {
    return null;
  }

  return <div className="block md:hidden">{renderMobileBlockContent()}</div>;
};

export const LivePlaylistMobileBlock = memo(LivePlaylistMobileBlockComponent);
